import type { JSX } from 'react';
import React, { useEffect, useMemo } from 'react';
import { Alert, Button, Col, Form, Row, Select, message } from 'antd';
import { useTranslation } from 'react-i18next';

import type { ReducedInternship, User } from '../../Types/interfaces';
import { StepName, StepStatus } from '../../Types/interfaces';
import {
  AssignStudentsToTutor,
  GetInternshipOffersOfOrganization,
  GetInternshipProcessesByOrganizations,
  GetStudentsAppliedToInternshipOffer,
  GetTutorsOfOrganization,
  GetUserById,
} from '../../helper/apiHelper';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { isUserDataFilled } from '../../helper/userHelper';

// to prevent infinity renders because [] !== [], every render create a new memory address
const EMPTY_INTERNSHIP_OFFERS_ARRAY: ReducedInternship[] = [];
const EMPTY_TUTORS_ARRAY: User[] = [];
const EMPTY_STUDENTS_APPLIED_TO_INTERNSHIP_OFFER_ARRAY: User[] = [];

interface TutorAssignmentComponentProps {
  recount: () => void;
}

const TutorAssignmentComponent = ({
  recount,
}: TutorAssignmentComponentProps): JSX.Element => {
  const loggedInUser = useCurrentUser();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const selectedOrganizationId = Form.useWatch('organization', form);
  const selectedOfferId = Form.useWatch('offer', form);
  const selectedTutorId = Form.useWatch('tutor', form);
  const selectedStudentId = Form.useWatch('students', form);
  const { data: userOrganizations } = useUserOrganizations(loggedInUser.id);
  const queryClient = useQueryClient();

  const { data: internshipOffers = EMPTY_INTERNSHIP_OFFERS_ARRAY } = useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_OFFERS_OF_ORGANIZATION,
      Number(selectedOrganizationId),
    ],
    queryFn: async () => {
      return await GetInternshipOffersOfOrganization(selectedOrganizationId);
    },
    enabled: !!selectedOrganizationId,
  });

  const { data: tutors = EMPTY_TUTORS_ARRAY } = useQuery({
    queryKey: [
      ...QUERY_KEYS.TUTORS_OF_ORGANIZATIONS,
      Number(selectedOrganizationId),
    ],
    queryFn: async () => {
      return await GetTutorsOfOrganization(selectedOrganizationId);
    },
    enabled: !!selectedOrganizationId,
  });

  const {
    data: studentsAppliedToInternshipOffer = EMPTY_STUDENTS_APPLIED_TO_INTERNSHIP_OFFER_ARRAY,
  } = useQuery({
    queryKey: [
      ...QUERY_KEYS.STUDENTS_APPLIED_TO_INTERNSHIP_OFFER,
      selectedOrganizationId,
      selectedOfferId,
    ],
    queryFn: async () => {
      return await GetStudentsAppliedToInternshipOffer(
        selectedOrganizationId,
        selectedOfferId
      );
    },
    enabled: !!selectedOrganizationId && !!selectedOfferId,
  });

  // processes of the organization, to see which students still have to fill out the work safety form
  const { data: organizationProcesses } = useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ORGANIZATIONS,
      Number(selectedOrganizationId),
    ],
    queryFn: async () => {
      return await GetInternshipProcessesByOrganizations([
        Number(selectedOrganizationId),
      ]);
    },
    enabled: !!selectedOrganizationId,
  });

  // students whose tutor cannot be assigned yet: the work safety form (university internships) is not done.
  // They are not in the applicants list below (the backend leaves them out), so they come from their processes.
  const waitingStudents = useMemo(() => {
    const byStudentId = new Map<number, string>();
    organizationProcesses?.forEach((process) => {
      const workSafetyStep = process.processSteps?.find(
        (step) => step.stepName === StepName.workSafetyInstructionReading
      );
      if (
        workSafetyStep &&
        !workSafetyStep.disabled &&
        workSafetyStep.stepStatus !== StepStatus.finished &&
        !process.tutorId
      ) {
        byStudentId.set(process.studentId, process.studentName);
      }
    });
    return Array.from(byStudentId, ([id, name]) => ({ id, name }));
  }, [organizationProcesses]);
  // default automatically select if only one data have
  useEffect(() => {
    if (userOrganizations?.length === 1 && !selectedOrganizationId) {
      form.setFieldValue('organization', userOrganizations[0].id);
    }
  }, [form, selectedOrganizationId, userOrganizations]);

  useEffect(() => {
    if (internshipOffers.length === 1 && !selectedOfferId) {
      form.setFieldValue('offer', internshipOffers[0].id);
    }
  }, [form, internshipOffers, selectedOfferId]);

  useEffect(() => {
    if (tutors.length === 1) {
      form.setFieldValue('tutor', tutors[0].id);
    }
  }, [form, tutors]);

  useEffect(() => {
    if (studentsAppliedToInternshipOffer.length === 1) {
      form.setFieldValue('students', [studentsAppliedToInternshipOffer[0].id]);
    }
  }, [form, studentsAppliedToInternshipOffer]);

  const assignStudentsToTutorMutation = useMutation({
    mutationFn: () =>
      AssignStudentsToTutor(
        selectedOrganizationId,
        selectedOfferId,
        selectedTutorId,
        selectedStudentId
      ),
    onSuccess: () => {
      messageApi.open({
        type: 'success',
        content: t('tutorAssignmentComp.addSuccess'),
      });
      queryClient.invalidateQueries({
        queryKey: [
          ...QUERY_KEYS.STUDENTS_APPLIED_TO_INTERNSHIP_OFFER,
          selectedOrganizationId,
          selectedOfferId,
        ],
      });
      form.resetFields(['students']);
      recount();
    },
    onError: (error) => {
      console.log(error);
      messageApi.open({
        type: 'error',
        content: t('tutorAssignmentComp.addError'),
      });
    },
  });

  return (
    <>
      <br />
      {contextHolder}
      {waitingStudents.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message={t('tutorAssignmentComp.waitingForWorkSafetyTitle')}
          description={t(
            'tutorAssignmentComp.waitingForWorkSafetyDescription',
            {
              names: waitingStudents.map((student) => student.name).join(', '),
            }
          )}
        />
      )}
      <Form form={form} size="large">
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item name="organization">
              <Select
                size="large"
                placeholder={t('tutorAssignmentComp.selOrg')}
                options={userOrganizations?.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                onChange={() =>
                  form.setFieldsValue({
                    tutor: undefined,
                    offer: undefined,
                    students: undefined,
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="offer">
              <Select
                size="large"
                placeholder={t('tutorAssignmentComp.selOffer')}
                options={internshipOffers.map((o) => ({
                  label: o.name,
                  value: o.id,
                }))}
                disabled={!selectedOrganizationId}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item name="tutor">
              <Select
                showSearch
                placeholder={t('tutorAssignmentComp.selTutor')}
                options={tutors?.map((tutor) => ({
                  label:
                    tutor.firstName === null || tutor.lastName === null
                      ? tutor.email
                      : `${tutor.firstName} ${tutor.lastName}`.trim(),
                  value: tutor.id,
                }))}
                disabled={!selectedOrganizationId}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="students">
              <Select
                mode="multiple"
                allowClear
                placeholder={t('tutorAssignmentComp.selApplicants')}
                options={studentsAppliedToInternshipOffer?.map((user) => ({
                  label: `${user.lastName} ${user.firstName}`.trim(),
                  value: user.id,
                }))}
                disabled={!selectedOfferId}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={async () => {
                  const user = await GetUserById(selectedTutorId);
                  if (!isUserDataFilled(user)) {
                    messageApi.warning(
                      t('tutorAssignmentComp.userDataMissingWarning'),
                      7
                    );
                    return;
                  }
                  assignStudentsToTutorMutation.mutate();
                }}
              >
                {t('tutorAssignmentComp.assignToTutor')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default TutorAssignmentComponent;
